import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../../context/AuthContext';
import { MonoLabel, EmptyState } from '../../Editorial';
import { beaconOpened } from '../../../lib/recruitingBeacons';
import { buildSchoolEvents, filterEvents } from '../../../lib/schoolEvents';
import { parseRailFilter } from '../../../lib/recruitParams';
import FilterChips from './FilterChips';
import RailRow from './RailRow';
import PanelModal from './PanelModal';
import AddEventMenu from './AddEventMenu';
import OffersPanel from '../OffersPanel';
import VisitsPanel from '../VisitsPanel';
import FollowingPanel from '../FollowingPanel';
import './rail.css';

/**
 * The unified school activity rail — every offer, visit and follow on one
 * dated timeline, under filter chips.
 *
 * Replaces the Schools pill tabs F2 shipped as placeholders. Mirrors the
 * Schools rail in RecruitSchoolsView.swift / RecruitSchoolsScreen.kt.
 *
 * Owns all three fetches so the chips and the rows are fed from one built
 * list and cannot disagree about what exists.
 *
 * Spec: docs/superpowers/specs/2026-08-10-web-recruit-activity-rail-design.md
 */

// event.kind -> the bucket its panel and beacon belong to.
const KIND_TO_BUCKET = { offer: 'offers', visit: 'visits', following: 'following' };

const PANELS = {
  offers: { Component: OffersPanel, title: 'Offers' },
  visits: { Component: VisitsPanel, title: 'Visits' },
  following: { Component: FollowingPanel, title: 'Following' },
};

export default function ActivityRail() {
  const { API_URL } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState({ offers: [], visits: [], following: [] });
  const [loading, setLoading] = useState(true);
  const [openBucket, setOpenBucket] = useState(null);
  const [showAdd, setShowAdd] = useState(false);

  const filter = parseRailFilter(searchParams);

  const load = useCallback(async () => {
    const cfg = { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } };
    // One failing bucket must not blank the whole rail, so each degrades to [].
    const safe = (p) => p.then((r) => r.data || []).catch(() => []);
    const [offers, visits, following] = await Promise.all([
      safe(axios.get(`${API_URL}/athletes/offers`, cfg)),
      safe(axios.get(`${API_URL}/athletes/visits`, cfg)),
      safe(axios.get(`${API_URL}/athletes/following`, cfg)),
    ]);
    return { offers, visits, following };
  }, [API_URL]);

  useEffect(() => {
    let cancelled = false;
    load().then((next) => {
      if (cancelled) return;
      setData(next);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [load]);

  const events = useMemo(() => buildSchoolEvents(data), [data]);
  const shown = useMemo(() => filterEvents(events, filter), [events, filter]);

  const setFilter = (next) => {
    const params = new URLSearchParams(searchParams);
    params.set('filter', next);
    // A legacy ?tab= would keep overriding nothing, but leaving it around makes
    // the URL lie about what is showing — drop it once the athlete picks.
    params.delete('tab');
    setSearchParams(params, { replace: true });
  };

  const openEvent = (event) => {
    const bucket = KIND_TO_BUCKET[event.kind];
    if (!bucket) return;
    // The apps beacon when a row opens an editor, not only on Add.
    beaconOpened(API_URL, bucket);
    setOpenBucket(bucket);
  };

  const pickAdd = (bucket) => {
    setShowAdd(false);
    beaconOpened(API_URL, bucket);
    setOpenBucket(bucket);
  };

  // An edit made in the modal should be visible on the rail behind it.
  const closePanel = () => {
    setOpenBucket(null);
    load().then(setData);
  };

  const panel = openBucket ? PANELS[openBucket] : null;

  return (
    <div className="recruit-rail">
      <div className="rail-head">
        <MonoLabel>Your Activity</MonoLabel>
        <button
          type="button"
          className="rail-add"
          onClick={() => setShowAdd(true)}
          data-testid="rail_add"
        >
          + Add
        </button>
      </div>

      <FilterChips events={events} active={filter} onChange={setFilter} />

      {loading && (
        <p className="ed-muted" style={{ padding: '8px 0' }}>Loading your recruiting activity…</p>
      )}

      {!loading && shown.length === 0 && (
        <EmptyState
          title={events.length === 0 ? 'Nothing logged yet.' : 'Nothing in this filter.'}
          body={events.length === 0
            ? 'Follow a school, add a visit, or log an offer — it all shows up here.'
            : 'Try another filter, or add something with the button above.'}
        />
      )}

      {!loading && shown.length > 0 && (
        <div className="rail-list">
          {shown.map((event) => (
            <RailRow key={event.id} event={event} onOpen={openEvent} />
          ))}
        </div>
      )}

      {showAdd && (
        <PanelModal title="Add to your recruiting" onClose={() => setShowAdd(false)}>
          <MonoLabel>Add to your recruiting</MonoLabel>
          <AddEventMenu onPick={pickAdd} />
        </PanelModal>
      )}

      {panel && (
        <PanelModal title={panel.title} onClose={closePanel}>
          <panel.Component />
        </PanelModal>
      )}
    </div>
  );
}
